import React, { useState, useEffect } from 'react';
import { Save, AlertTriangle } from 'lucide-react';
import { useProfile } from '../../hooks/useProfile';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import AvatarSelector from './AvatarSelector';

const ProfileDetails = () => {
  const { user } = useAuth();
  const { profile, updateProfile, isLoading, error } = useProfile();
  const [formData, setFormData] = useState({
    display_name: '',
    avatar_url: ''
  });
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [displayNameAvailable, setDisplayNameAvailable] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (profile) {
      setFormData({
        display_name: profile.display_name || '',
        avatar_url: profile.avatar_url || ''
      });
      setHasChanges(false);
    }
  }, [profile]);

  const validateDisplayName = (name: string): boolean => {
    // Must be 3-30 characters and only contain letters, numbers, underscores, and hyphens
    const validFormat = /^[a-zA-Z0-9_-]{3,30}$/;
    return validFormat.test(name);
  };

  const handleDisplayNameChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
    
    // Reset validation states
    setDisplayNameAvailable(true);
    setUpdateError(null);

    // Return early if empty or unchanged
    if (!value || value === profile?.display_name) {
      return;
    }

    // Validate format first
    if (!validateDisplayName(value)) {
      setDisplayNameAvailable(false);
      setUpdateError('Display name must be 3-30 characters and contain only letters, numbers, underscores, and hyphens');
      return;
    }

    try {
      const { data, error } = await supabase
        .rpc('check_display_name_available', { 
          name: value,
          user_id: user?.id || ''
        });
      
      if (error) throw error;
      
      setDisplayNameAvailable(data);
      setUpdateError(data ? null : 'This display name is already taken');
    } catch (err) {
      console.error('Error checking display name:', err);
      setDisplayNameAvailable(false);
      setUpdateError('Error checking display name availability');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    
    // Validate display name before submitting
    if (!validateDisplayName(formData.display_name)) {
      setUpdateError('Invalid display name format');
      return;
    }
    
    try {
      setUpdateError(null);
      await updateProfile(formData);
      setHasChanges(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update profile';
      if (message.includes('unique constraint')) {
        setUpdateError('This display name is already taken. Please choose another.');
      } else if (message.includes('valid_display_name')) {
        setUpdateError('Invalid display name format. Use only letters, numbers, underscores, and hyphens.');
      } else {
        setUpdateError(message);
      }
    }
  };

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-500 p-6 rounded-lg flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
        <div>
          <h3 className="font-medium mb-1">Error</h3>
          <p className="text-sm">{error.message}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-sm underline mt-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-1">Email</label>
        <input
          type="email"
          value={user?.email || ''}
          disabled
          className="w-full px-4 py-2 bg-background border border-gray-800 rounded-lg opacity-50"
        />
        <p className="mt-1 text-xs text-content-secondary">
          Email cannot be changed
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Avatar</label>
        <AvatarSelector
          value={formData.avatar_url}
          onChange={(url) => {
            setFormData(prev => ({ ...prev, avatar_url: url }));
            setHasChanges(true);
          }}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Display Name</label>
        <input
          type="text"
          name="display_name"
          value={formData.display_name}
          onChange={handleDisplayNameChange}
          className={`w-full px-4 py-2 bg-background border rounded-lg transition-colors ${
            !displayNameAvailable 
              ? 'border-red-500 focus:border-red-500' 
              : 'border-gray-800 focus:border-brand'
          }`}
        />
        {!displayNameAvailable && updateError && (
          <p className="mt-1 text-sm text-red-500">
            {updateError}
          </p>
        )}
        <p className="mt-1 text-xs text-content-secondary">
          3-30 characters, letters, numbers, underscores, and hyphens only
        </p>
      </div>

      {updateError && displayNameAvailable && (
        <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
          {updateError}
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={isLoading || !displayNameAvailable || !hasChanges}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4 mr-2" />
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
};

export default ProfileDetails;